import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { Button, Label, Input, Switch } from "@cieda/djayma-ui";
import { Info } from "lucide-react";
import { z } from "zod";
import { AdminModal } from "../shared/AdminModal";
import { AdminForm } from "../shared/AdminForm";
import {
  useCreateAlertRule,
  useUpdateAlertRule,
} from "@/hooks/admin/useStockAlerts";
import type { StockAlertRule, CreateStockAlertRuleInput } from "@/lib/admin-api/inventory";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const alertRuleSchema = z
  .object({
    scope: z.enum(["product", "category"]),
    product_id: z.string().optional(),
    category_id: z.string().optional(),
    threshold: z.coerce.number().int().min(0),
    notify_email: z.boolean(),
    notify_sms: z.boolean(),
    notify_whatsapp: z.boolean(),
    auto_reorder: z.boolean(),
    is_active: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.scope === "product") return !!data.product_id;
      return !!data.category_id;
    },
    {
      message: "Veuillez s\u00e9lectionner un produit ou une cat\u00e9gorie",
      path: ["product_id"],
    },
  );

type AlertRuleFormValues = z.infer<typeof alertRuleSchema>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AlertRuleFormProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly rule: StockAlertRule | null;
}

// ---------------------------------------------------------------------------
// Inner form fields (needs FormProvider context)
// ---------------------------------------------------------------------------

function AlertRuleFields() {
  const { t } = useTranslation("admin");
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AlertRuleFormValues>();

  const scope = watch("scope");
  const notifyEmail = watch("notify_email");
  const notifySms = watch("notify_sms");
  const notifyWhatsapp = watch("notify_whatsapp");
  const autoReorder = watch("auto_reorder");
  const isActive = watch("is_active");

  return (
    <div className="space-y-5">
      {/* Scope radio */}
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-stone-700">
          {t("stock_alerts.field_scope")}
        </legend>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <input
              type="radio"
              value="product"
              {...register("scope")}
              className="accent-amber-600"
            />
            {t("stock_alerts.scope_product")}
          </label>
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <input
              type="radio"
              value="category"
              {...register("scope")}
              className="accent-amber-600"
            />
            {t("stock_alerts.scope_category")}
          </label>
        </div>
      </fieldset>

      {/* Product select (shown when scope === product) */}
      {scope === "product" && (
        <div>
          <Label htmlFor="product_id">{t("stock_alerts.field_product")}</Label>
          <Input
            id="product_id"
            placeholder={t("stock_alerts.product_placeholder")}
            {...register("product_id")}
            aria-invalid={!!errors.product_id}
          />
          {errors.product_id && (
            <p className="mt-1 text-xs text-red-600">{errors.product_id.message}</p>
          )}
        </div>
      )}

      {/* Category select (shown when scope === category) */}
      {scope === "category" && (
        <div>
          <Label htmlFor="category_id">{t("stock_alerts.field_category")}</Label>
          <Input
            id="category_id"
            placeholder={t("stock_alerts.category_placeholder")}
            {...register("category_id")}
            aria-invalid={!!errors.category_id}
          />
          {errors.category_id && (
            <p className="mt-1 text-xs text-red-600">{errors.category_id.message}</p>
          )}
        </div>
      )}

      {/* Threshold */}
      <div>
        <Label htmlFor="threshold">{t("stock_alerts.field_threshold")}</Label>
        <Input
          id="threshold"
          type="number"
          min={0}
          step={1}
          {...register("threshold", { valueAsNumber: true })}
          aria-invalid={!!errors.threshold}
        />
        {errors.threshold && (
          <p className="mt-1 text-xs text-red-600">{errors.threshold.message}</p>
        )}
      </div>

      {/* Notification channels */}
      <fieldset>
        <legend className="mb-3 text-sm font-medium text-stone-700">
          {t("stock_alerts.field_channels")}
        </legend>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="notify_email" className="text-sm">
              Email
            </Label>
            <Switch
              id="notify_email"
              checked={notifyEmail}
              onCheckedChange={(v: boolean) => setValue("notify_email", v)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="notify_sms" className="text-sm">
              SMS
            </Label>
            <Switch
              id="notify_sms"
              checked={notifySms}
              onCheckedChange={(v: boolean) => setValue("notify_sms", v)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="notify_whatsapp" className="text-sm">
              WhatsApp
            </Label>
            <Switch
              id="notify_whatsapp"
              checked={notifyWhatsapp}
              onCheckedChange={(v: boolean) => setValue("notify_whatsapp", v)}
            />
          </div>
        </div>
      </fieldset>

      {/* Auto-reorder toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Label htmlFor="auto_reorder" className="text-sm">
            {t("stock_alerts.field_auto_reorder")}
          </Label>
          <span
            title={t("stock_alerts.auto_reorder_tooltip")}
            className="cursor-help"
          >
            <Info className="h-3.5 w-3.5 text-stone-400" />
          </span>
        </div>
        <Switch
          id="auto_reorder"
          checked={autoReorder}
          onCheckedChange={(v: boolean) => setValue("auto_reorder", v)}
        />
      </div>

      {/* Active toggle */}
      <div className="flex items-center justify-between">
        <Label htmlFor="is_active" className="text-sm">
          {t("stock_alerts.field_active")}
        </Label>
        <Switch
          id="is_active"
          checked={isActive}
          onCheckedChange={(v: boolean) => setValue("is_active", v)}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal wrapper
// ---------------------------------------------------------------------------

export function AlertRuleForm({ open, onClose, rule }: AlertRuleFormProps) {
  const { t } = useTranslation("admin");
  const createRule = useCreateAlertRule();
  const updateRule = useUpdateAlertRule();

  const isEditing = rule !== null;

  const defaultValues = useMemo<AlertRuleFormValues>(
    () => ({
      scope: rule?.scope ?? "product",
      product_id: rule?.product_id ?? "",
      category_id: rule?.category_id ?? "",
      threshold: rule?.threshold ?? 10,
      notify_email: rule?.notify_email ?? false,
      notify_sms: rule?.notify_sms ?? false,
      notify_whatsapp: rule?.notify_whatsapp ?? false,
      auto_reorder: rule?.auto_reorder ?? false,
      is_active: rule?.is_active ?? true,
    }),
    [rule],
  );

  const handleSubmit = async (values: AlertRuleFormValues) => {
    const payload: CreateStockAlertRuleInput = {
      scope: values.scope,
      ...(values.scope === "product"
        ? { product_id: values.product_id }
        : { category_id: values.category_id }),
      threshold: values.threshold,
      notify_email: values.notify_email,
      notify_sms: values.notify_sms,
      notify_whatsapp: values.notify_whatsapp,
      auto_reorder: values.auto_reorder,
      is_active: values.is_active,
    };

    if (isEditing && rule) {
      await updateRule.mutateAsync({ id: rule.id, data: payload });
    } else {
      await createRule.mutateAsync(payload);
    }

    onClose();
  };

  const title = isEditing
    ? t("stock_alerts.edit_rule_title")
    : t("stock_alerts.create_rule_title");

  return (
    <AdminModal open={open} onClose={onClose} title={title} size="md">
      <AdminForm<AlertRuleFormValues>
        schema={alertRuleSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        submitLabel={isEditing ? t("actions.save") : t("actions.create")}
      >
        <AlertRuleFields />
      </AdminForm>
    </AdminModal>
  );
}
